/** Per-slot repaint + user actions — port of refreshSlot/onAct/onSt/onDur. */
import { STATUS_LABELS, applyActChange, bookable, checklistToggleCascade, computeChecklist, cycleStatus } from "@trip/shared/engine/checklist";
import { defDur, fitDayToTarget } from "@trip/shared/engine/schedule";
import { fdur, t12 } from "@trip/shared/engine/time";
import { esc } from "@trip/shared/engine/time";
import type { ChangeEntry } from "@trip/shared/protocol";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { sendChanges } from "../sync.js";
import { ico } from "./icons.js";
import { confirmLockedChange } from "./lockpop.js";

/** Extra slots ("x1"…) have string indexes; definition slots have numbers. */
export type SlotIndex = number | string;
const isExtra = (i: SlotIndex): i is string => typeof i === "string";

export function refreshSlot(dayId: string, i: SlotIndex) {
  const def = store.def;
  const st = store.slots[`${dayId}_${i}`];

  // an extra row that nobody has filled yet: just the picker, everything else hidden
  if (!st && isExtra(i)) {
    const actBtn = document.getElementById(`act_${dayId}_${i}`) as HTMLButtonElement | null;
    if (actBtn) {
      actBtn.textContent = "＋ Add an activity";
      actBtn.disabled = store.role !== "edit";
    }
    for (const id of [`dur_${dayId}_${i}`, `st_${dayId}_${i}`, `dir_${dayId}_${i}`, `note_${dayId}_${i}`]) {
      const el = document.getElementById(id);
      if (el) el.style.display = "none";
    }
    const meta = document.getElementById(`meta_${dayId}_${i}`);
    if (meta) meta.innerHTML = "";
    return;
  }
  if (!st) return;

  // a filled extra row is a normal slot — the blank row's ✨ Suggest hides
  if (isExtra(i)) {
    const sug = document.getElementById(`sug_${dayId}_${i}`);
    if (sug) sug.style.display = "none";
  }

  const a = def.act[st.act];
  const sb = document.getElementById(`st_${dayId}_${i}`)!;
  sb.textContent = STATUS_LABELS[st.st];
  sb.className = "btn-st st" + st.st;
  sb.setAttribute(
    "title",
    bookable(def, st.act) ? "Cycle: Deciding → Confirmed → Booked" : "Cycle: Deciding → Confirmed (walk-up — nothing to book)"
  );
  const row = document.getElementById(`row_${dayId}_${i}`)!;
  row.className = "row" + (isExtra(i) ? " xrow" : "") + (st.st ? " rst" + st.st : "");
  const actBtn = document.getElementById(`act_${dayId}_${i}`) as HTMLButtonElement;
  actBtn.textContent = a.name;
  actBtn.disabled = st.st === 2 || store.role !== "edit";
  const durSel = document.getElementById(`dur_${dayId}_${i}`) as HTMLSelectElement;
  durSel.innerHTML =
    a.durs.map((x) => `<option value="${x}" ${x === st.dur ? "selected" : ""}>${fdur(x)}</option>`).join("") +
    // extras can be emptied again; a definition slot's skip lives in its own options
    (isExtra(i) && !a.durs.includes(0) ? `<option value="0" ${st.dur === 0 ? "selected" : ""}>— remove —</option>` : "");
  durSel.style.display = isExtra(i) ? "" : a.durs.length > 1 ? "" : a.durs[0] === 0 ? "none" : "";
  durSel.disabled = st.st === 2 || store.role !== "edit";
  const stBtn = document.getElementById(`st_${dayId}_${i}`);
  if (stBtn) stBtn.style.display = "";
  const dirBtn = document.getElementById(`dir_${dayId}_${i}`);
  if (dirBtn) dirBtn.style.display = "";
  const det = document.getElementById(`det_${dayId}_${i}`)!;
  // icon only: the word "Details" made this the widest control in the row and
  // pushed the status button out of alignment from row to row
  det.innerHTML = a.nomodal
    ? ""
    : `<button class="btn-det btn-ico btn-details" data-action="modal" data-id="${st.act}" title="Details" aria-label="Details">${ico.info}</button>`;
  const dayObj = def.days.find((d) => d.id === dayId);
  const item = isExtra(i) ? undefined : dayObj?.items[i];
  // An item's note was written about its DEFAULT recommendation ("Van Gogh =
  // timed entry, book now!") — showing it under a different pick presents
  // stale advice as if it applied. It returns when the default is re-chosen.
  const isDefaultPick = item && !isFixed(item) && st.act === (item.def ?? item.opts[0]);
  const fallbackNote = (isDefaultPick && !isFixed(item!) && item!.note) || "";
  // At a glance the note is a chip you hover or tap; the full text prints
  // (paper is exactly where "book now!" advice belongs) and stays in the
  // details card. On screen it stops competing with the schedule.
  const txt = t12(a.slotnote || fallbackNote);
  const noteEl = document.getElementById(`note_${dayId}_${i}`);
  if (noteEl) {
    noteEl.textContent = txt;
    (noteEl as HTMLElement).style.display = txt ? "" : "none";
  }
  const noteWrap = document.getElementById(`notew_${dayId}_${i}`);
  if (noteWrap) {
    noteWrap.innerHTML = txt
      ? `<button type="button" class="notechip" data-tip="${esc(txt)}" aria-label="Note">${ico.note}</button>`
      : "";
  }
  // price and booking badges live on the left rail (times.ts railFare) —
  // the meta line under the dropdown keeps opening hours only
  const meta = document.getElementById(`meta_${dayId}_${i}`)!;
  meta.innerHTML = a.nomodal || !a.hours ? "" : `<span class="hrs">🕒 ${esc(a.hours.split("·")[0].trim())}</span>`;
  // mirrored plain-text value for print
  let pv = row.querySelector<HTMLElement>(".print-value");
  if (!pv) {
    pv = document.createElement("div");
    pv.className = "print-value";
    row.querySelector(".slot")!.insertBefore(pv, row.querySelector(".ctrls"));
  }
  pv.textContent = `${a.name}${st.dur ? ` · ${fdur(st.dur)}` : ""} · ${STATUS_LABELS[st.st]}`;
}

/**
 * All slot edits funnel through here: build the full cascade (slot + implied
 * checklist flips) as ONE batch so observers never see half of it.
 */
function commit(entries: ChangeEntry[]) {
  sendChanges(entries);
}

/** After slot status edits, persist any checks the checklist logic implies. */
function withImpliedChecks(entries: ChangeEntry[]): ChangeEntry[] {
  const slots = { ...store.slots };
  for (const e of entries) if (e.kind === "slot") slots[e.key] = e.value as any;
  const { impliedChecks } = computeChecklist(store.def, slots, store.checks);
  for (const [k, on] of Object.entries(impliedChecks)) {
    if (store.checks[k] !== on) entries.push({ kind: "check", key: k, value: { on } });
  }
  return entries;
}

export function onAct(dayId: string, i: SlotIndex, val: string) {
  if (store.role !== "edit") return;
  const key = `${dayId}_${i}`;
  const cur = store.slots[key];

  // first pick in an extra row: no state exists until this moment
  if (!cur) {
    if (!val || !store.def.act[val]) return;
    commit(withImpliedChecks([{ kind: "slot", key, value: { act: val, dur: defDur(store.def, val), st: 0 } }]));
    return;
  }
  if (!val) return; // the placeholder is not a destination

  const apply = () => commit(withImpliedChecks([{ kind: "slot", key, value: applyActChange(store.def, store.slots[key], val) }]));
  // Confirmed picks are decisions already made — swapping one must be deliberate
  if (cur.st === 1 && val !== cur.act) {
    const sel = document.getElementById(`act_${dayId}_${i}`)!;
    confirmLockedChange(
      sel,
      `<b>${esc(store.def.act[cur.act].name)}</b> is ✔ Confirmed for this slot. Swap it out anyway?`
    ).then((ok) => (ok ? apply() : refreshSlot(dayId, i)));
    return;
  }
  apply();
}

export function onDur(dayId: string, i: SlotIndex, val: string) {
  if (store.role !== "edit") return;
  const key = `${dayId}_${i}`;
  const cur = store.slots[key];
  if (!cur) return;
  const apply = () => commit([{ kind: "slot", key, value: { ...store.slots[key], dur: parseFloat(val) } }]);
  if (cur.st === 1 && parseFloat(val) !== cur.dur) {
    const sel = document.getElementById(`dur_${dayId}_${i}`)!;
    confirmLockedChange(
      sel,
      `<b>${esc(store.def.act[cur.act].name)}</b> is ✔ Confirmed. Change its time anyway?`
    ).then((ok) => (ok ? apply() : refreshSlot(dayId, i)));
    return;
  }
  apply();
}

export function onSt(dayId: string, i: SlotIndex) {
  if (store.role !== "edit") return;
  const key = `${dayId}_${i}`;
  if (!store.slots[key]) return;
  const next = cycleStatus(store.def, store.slots[key]);
  commit(withImpliedChecks([{ kind: "slot", key, value: next }]));
}

export function onChecklistToggle(k: string, on: boolean) {
  if (store.role !== "edit") return;
  commit(checklistToggleCascade(store.def, store.slots, k, on));
}

/**
 * Changing a day's "back at base by" target immediately refits the day:
 * the target plus every slot the fitter moved go out as one batch.
 */
export function onDayTarget(dayId: string, minutes: number) {
  if (store.role !== "edit") return;
  const day = store.def.days.find((d) => d.id === dayId);
  if (!day) return;
  const entries: ChangeEntry[] = [{ kind: "dayend", key: dayId, value: { min: minutes } }];
  for (const c of fitDayToTarget(store.def, day, store.slots, minutes, store.fixDurs)) {
    entries.push({ kind: "slot", key: c.key, value: c.value });
  }
  commit(withImpliedChecks(entries));
  return entries.length - 1; // slots adjusted
}

/**
 * Sets (or clears, at 0) a duration override for an open-ended fixed item —
 * "passport control, budget 2h". No fit cascade: unlike the day-end target,
 * changing this doesn't reach for a flexible slot to compensate; the day just
 * reflows around the new effective end and its extra-slot slack updates.
 */
export function onFixDur(dayId: string, i: number, minutes: number) {
  if (store.role !== "edit") return;
  commit([{ kind: "fixdur", key: `${dayId}_${i}`, value: { min: minutes } }]);
}

/** Explicit "Fit to target" for the day's current target. */
export function onFitDay(dayId: string) {
  if (store.role !== "edit") return 0;
  const day = store.def.days.find((d) => d.id === dayId);
  if (!day) return 0;
  const target = store.dayEnds[dayId] ?? store.def.dayEnd;
  const changes = fitDayToTarget(store.def, day, store.slots, target, store.fixDurs);
  if (!changes.length) return 0;
  commit(withImpliedChecks(changes.map((c) => ({ kind: "slot" as const, key: c.key, value: c.value }))));
  return changes.length;
}
